<template>
  <div class="my-addresses-container responsive-p">
    <NavigationBar :items="navItems" />
    <div class="top-header-wrapper">
      <button @click="back" v-if="!isDesktop">
        <img src="@/assets/icons/primary-back.svg" alt="back">
      </button>
      <p class="title">{{ t('myAddresses.title') }}</p>
    </div>
    <div class="content-wrapper" :class="{ 'is-empty': addresses.length === 0 }">
      <div class="no-item-wrapper" v-if="addresses.length === 0">
        <img src="@/assets/images/address.svg" alt="address">
        <p class="no-item-text">
          {{  t('myAddresses.noItem') }}
        </p>
      </div>
      <div class="items-wrapper" v-else-if="addresses.length > 0">
        <AddressCard
          v-for="address in addresses"
          :key="address.id"
          :address="address"
        />
      </div>
      <button @click="goToAddAddress" class="add-button">
        <img src="@/assets/icons/plus.svg" alt="add">
      </button>
    </div>
  </div>
</template>

<script>
import { onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useScreenSize } from '@/utils/isDesktop'

import AddressCard from '@/components/AddressCard'
import NavigationBar from '@/components/NavigationBar'

export default {
  name: 'MyAddressView',
  components: {
    AddressCard,
    NavigationBar,
  },
  setup() {
    /** context */
    const store = useStore()
    const router = useRouter()
    const { t } = useI18n()
    const { isDesktop } = useScreenSize()

    /** computed */
    const addresses = computed(() => store.state.user.addresses || [])
    const navItems = computed(() => [
      {
        name: t('menu.home'),
        path: '/'
      },
      {
        name: t('menu.profile'),
        path: '/my-profile'
      },
      {
        name: t('profileMenu.address')
      }
    ])
    
    /** method */
    const back = () => router.back()
    const goToAddAddress = () => router.push('/my-addresses/new')

    /** mounted */
    onMounted(() => {
      store.dispatch('user/fetchGetAddresses')
    })

    return {
      t,
      back,
      goToAddAddress,
      addresses,
      isDesktop,
      navItems,
    }
  }
}
</script>

<style lang="scss" scoped>

.my-addresses-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  padding-top: 56px;
  @media screen and (min-width: 780px) {
    padding-top: 0;
    padding-bottom: 0;
    height: calc(100% - 74px);
    margin-top: 24px;
  }
}

.top-header-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  border-bottom: 1px solid #D6E2EF;
  background-color: var(--white-color);
  position: sticky;
  top: 0;
  z-index: 10;
  flex-shrink: 0;
  @media screen and (min-width: 780px) {
    position: relative;
    border-bottom: 0;
  }
  > button {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    font-weight: bold;
    font-size: 20px;
    color: var(--secondary-color);
    outline: none;
    background-color: transparent;
    border: 0;
    padding: 12px;
  }
}

.title {
  font-size: 24px;
  font-weight: bold;
}

.desktop-title {
  font-size: 24px;
  font-weight: bold;
}

// Holds whichever block is showing plus the add button, so the button always sits
// directly after the content rather than at the bottom of the page.
.content-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  // With nothing saved yet, the icon, the message and the button centre together
  // as one group.
  &.is-empty {
    align-items: center;
    justify-content: center;
  }
}

.no-item-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.items-wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
}

.add-button {
  align-self: center;
  margin: 24px 0 32px;
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border: 0;
  border-radius: 50%;
  padding: 0;
  outline: none;
  cursor: pointer;
  // No 300ms tap delay and no double-tap zoom swallowing the press.
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  background-color: var(--secondary-color);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.26);
  }
  &:active {
    transform: scale(0.94);
  }
  > img {
    width: 22px;
    height: 22px;
    // Every press lands on the button, never on the icon inside it.
    pointer-events: none;
  }
}

.no-item-text {
  font-size: 20px;
  margin-top: 16px;
}
</style>
